import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Mail, RefreshCw, CheckCircle, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    
    const inputRefs = useRef([]);
    const email = location.state?.email || user?.email || '';
    
    useEffect(() => {
        // Focus first input on mount
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);
    
    useEffect(() => {
        // Cooldown timer for resend
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);
    
    const handleChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) return;
        
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError('');
        
        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
        
        // Auto-submit when all digits entered
        if (value && index === 5) {
            const fullCode = newCode.join('');
            if (fullCode.length === 6) {
                handleVerify(fullCode);
            }
        }
    };
    
    const handleKeyDown = (index, e) => {
        // Handle backspace - move to previous input
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        
        // Handle paste
        if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            navigator.clipboard.readText().then(text => {
                const digits = text.replace(/\D/g, '').slice(0, 6).split('');
                const newCode = [...code];
                digits.forEach((digit, i) => {
                    if (i < 6) newCode[i] = digit;
                });
                setCode(newCode);
                if (digits.length === 6) {
                    handleVerify(digits.join(''));
                }
            });
        }
    };
    
    const handleVerify = async (verificationCode) => {
        if (!email) {
            setError('Email not found. Please register again.');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch(`${API_URL}/api/auth/verify-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code: verificationCode })
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.detail || 'Verification failed');
            }
            
            setSuccess(true);
            setTimeout(() => {
                navigate('/chat');
            }, 2000);
            
        } catch (err) {
            setError(err.message);
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };
    
    const handleResend = async () => {
        if (cooldown > 0 || !email) return;
        
        setResending(true);
        setError('');
        
        try {
            const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to resend code');
            }
            
            setCooldown(60); // 60 second cooldown
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
            
        } catch (err) {
            setError(err.message);
        } finally {
            setResending(false);
        }
    };
    
    if (success) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center"
                >
                    <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-primary" />
                    </div>
                    <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                        Email Verified!
                    </h2>
                    <p className="text-muted-foreground">
                        Redirecting you to the app...
                    </p>
                </motion.div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card className="border-border">
                        <CardContent className="p-8">
                            {/* Header */}
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <Mail className="w-8 h-8 text-primary" />
                                </div>
                                <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                                    Check your email
                                </h1>
                                <p className="text-muted-foreground text-sm">
                                    We sent a verification code to
                                </p>
                                <p className="text-foreground font-medium">
                                    {email || 'your email'}
                                </p>
                            </div>
                            
                            {/* Code Input */}
                            <div className="flex justify-center gap-2 mb-6">
                                {code.map((digit, index) => (
                                    <Input
                                        key={index}
                                        ref={el => inputRefs.current[index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        className="w-12 h-14 text-center text-2xl font-bold bg-background border-border focus:border-primary"
                                        disabled={loading}
                                        data-testid={`verify-code-${index}`}
                                    />
                                ))}
                            </div>
                            
                            {/* Error */}
                            {error && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-destructive text-sm text-center mb-4"
                                >
                                    {error}
                                </motion.p>
                            )}
                            
                            {/* Verify Button */}
                            <Button
                                onClick={() => handleVerify(code.join(''))}
                                disabled={code.join('').length !== 6 || loading}
                                className="w-full mb-4"
                                data-testid="verify-submit-btn"
                            >
                                {loading ? 'Verifying...' : 'Verify Email'}
                            </Button>
                            
                            {/* Resend */}
                            <div className="text-center">
                                <p className="text-sm text-muted-foreground mb-2">
                                    Didn't receive the code?
                                </p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleResend}
                                    disabled={cooldown > 0 || resending}
                                    className="text-primary hover:text-primary/80"
                                    data-testid="resend-code-btn"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${resending ? 'animate-spin' : ''}`} />
                                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
                                </Button>
                            </div>
                            
                            {/* Back to login */}
                            <div className="mt-6 pt-6 border-t border-border text-center">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/login')}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back to Login
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
}
